import { memo, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { UsersTableProps, UserV2 } from '../../../types/dashboardV2';
import { ROUTES } from '../../../constants/routes';
import {
  getUserStatusBadgeClass,
  getUserStatusDotClass,
  formatUserStatus,
  formatUserRole,
  getRoleBadgeClass,
} from '../../../shared/utils/statusUtils';
import {
  DataTable,
  type DataTableColumnMeta,
  type DataTableRenderers,
} from '../components/DataTable';
import { StatusBadgeCell, DateTimeCell } from '../components/cells';
import { usePrivacy } from '../../../context/PrivacyContext';
import { MASK, maskEmail, maskPhone } from '../../../shared/utils/mask';

/** Column shares below are shares of this; narrower viewports scroll the table instead. */
export const USERS_TABLE_MIN_WIDTH = '1060px';

export const USERS_COLUMNS = [
  { key: 'contact', labelKey: 'dashboard.tables.users.contact', width: '28.5%', cellClassName: 'cell-id', skeletonVariant: 'address' },
  { key: 'status', labelKey: 'dashboard.tables.users.status', width: '17%', skeletonVariant: 'status' },
  { key: 'balance', labelKey: 'dashboard.tables.users.balance', width: '14.5%', align: 'center', cellClassName: 'cell-price', skeletonVariant: 'price' },
  { key: 'role', labelKey: 'dashboard.tables.users.role', width: '12%', skeletonVariant: 'id' },
  { key: 'created', labelKey: 'dashboard.tables.users.created', width: '14%', align: 'center', skeletonVariant: 'id' },
  { key: 'updated', labelKey: 'dashboard.tables.users.updated', width: '14%', align: 'right', skeletonVariant: 'id' },
] as const satisfies readonly DataTableColumnMeta[];

const ContactCell = ({ user, hidden }: { user: UserV2; hidden: boolean }) => {
  const contactType = user.email ? 'email' : user.phone ? 'phone' : null;
  // Masked per kind: an email keeps a short head so rows stay tellable apart,
  // a phone keeps only its country code.
  const raw = user.email || user.phone || '-';
  const contactInfo = !hidden || !contactType
    ? raw
    : contactType === 'email'
      ? maskEmail(raw)
      : maskPhone(raw);
  return (
    <div className="user-contact-cell">
      {contactType && (
        <span className="material-symbols-outlined user-contact-icon" aria-hidden="true">
          {contactType === 'email' ? 'mail' : 'phone'}
        </span>
      )}
      <span>{contactInfo}</span>
    </div>
  );
};

export const UsersTable = memo(function UsersTable({
  users,
  total,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
}: UsersTableProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hidden } = usePrivacy('users');

  const handleRowClick = useCallback(
    (user: UserV2) => navigate(`/${ROUTES.DASHBOARD}/${ROUTES.DASH_USERS}/${user.id}`),
    [navigate],
  );

  const renderers = useMemo<DataTableRenderers<UserV2, typeof USERS_COLUMNS>>(
    () => ({
      contact: (u) => <ContactCell user={u} hidden={hidden} />,
      status: (u) => (
        <StatusBadgeCell
          badgeClass={getUserStatusBadgeClass(u.status)}
          dotClass={getUserStatusDotClass(u.status)}
          label={formatUserStatus(u.status)}
        />
      ),
      balance: (u) => (hidden ? MASK : `${u.balance.toFixed(2)} ₴`),
      role: (u) => (
        <span className={`role-badge ${getRoleBadgeClass(u.role)}`}>{formatUserRole(u.role)}</span>
      ),
      created: (u) => <DateTimeCell value={u.createdAt} center />,
      updated: (u) => <DateTimeCell value={u.updatedAt} />,
    }),
    [hidden],
  );

  return (
    <DataTable
      columns={USERS_COLUMNS}
      renderers={renderers}
      rows={users}
      rowKey={(u) => u.id}
      minWidth={USERS_TABLE_MIN_WIDTH}
      pagination={{ total, page, rowsPerPage, onPageChange, onRowsPerPageChange }}
      onRowClick={handleRowClick}
      rowAriaLabel={(u) =>
        t('dashboard.tables.users.viewAria', { id: u.email || u.phone || u.id })
      }
    />
  );
});

export default UsersTable;
